import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "../AdminCard";
import { StatusBadge, ProgressBar } from "./CampaignStatus";
import { warsawTime } from "./templateUtils";
import CampaignDetailModal from "./CampaignDetailModal";

const isEditable = (c) => ["draft", "scheduled"].includes(c.status) || (c.status === "failed" && !c.total);

export const CampaignList = ({ campaigns, reload, onEditDraft }) => {
    const [openId, setOpenId] = useState(null);
    const anySending = campaigns.some((c) => c.status === "sending");

    useEffect(() => {
        if (!anySending) return undefined;
        const t = setInterval(reload, 3000);
        return () => clearInterval(t);
    }, [anySending, reload]);

    const edit = async (id) => {
        try { const { data } = await api.get(`/mailing/campaigns/${id}`); onEditDraft(data); }
        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const del = async (id) => {
        if (!window.confirm("Usunąć kampanię wraz z historią wysyłki?")) return;
        try { await api.delete(`/mailing/campaigns/${id}`); toast.success("Usunięto."); reload(); }
        catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <AdminCard>
            <h3 className="font-serif text-xl text-[#092E22] mb-5">Historia kampanii ({campaigns.length})</h3>
            <div className="space-y-2 max-h-[900px] overflow-y-auto" data-testid="mailing-campaign-list">
                {campaigns.length === 0 && <p className="text-sm text-gray-400">Brak kampanii.</p>}
                {campaigns.map((c) => (
                    <div key={c.id} className="p-4 rounded-2xl bg-[#FAF8F3] border border-gray-100 hover:border-[#0D4F3C]/30 transition-colors" data-testid={`mailing-campaign-${c.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <button className="text-left min-w-0 flex-1" onClick={() => (isEditable(c) ? edit(c.id) : setOpenId(c.id))} data-testid={`mailing-campaign-open-${c.id}`}>
                                <div className="flex items-center gap-2 mb-1"><StatusBadge status={c.status} testid={`mailing-campaign-status-${c.id}`} /></div>
                                <p className="font-semibold text-sm text-[#092E22] truncate">{c.subject}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {c.status === "scheduled" ? `Wysyłka: ${warsawTime(c.scheduled_at)}` : new Date(c.sent_at || c.created_at).toLocaleString("pl-PL")}
                                    {!["draft", "scheduled"].includes(c.status) && ` · ${c.sent || 0}/${c.total || 0} wysłano${c.failed ? ` · ${c.failed} błędów` : ""}`}
                                </p>
                            </button>
                            <div className="flex items-center gap-1 shrink-0">
                                {isEditable(c) && (
                                    <button onClick={() => edit(c.id)} className="p-1.5 rounded-lg text-[#0D4F3C] hover:bg-white" data-testid={`mailing-campaign-edit-${c.id}`}><Pencil size={15} /></button>
                                )}
                                {c.status !== "sending" && (
                                    <button onClick={() => del(c.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-white" data-testid={`mailing-campaign-delete-${c.id}`}><Trash2 size={15} /></button>
                                )}
                            </div>
                        </div>
                        {c.error && !["sending"].includes(c.status) && <p className="text-[11px] text-red-600 mt-2" data-testid={`mailing-campaign-error-${c.id}`}>{c.error}</p>}
                        {!["draft", "scheduled"].includes(c.status) && <div className="mt-3"><ProgressBar sent={c.sent} failed={c.failed} total={c.total} /></div>}
                    </div>
                ))}
            </div>
            {openId && <CampaignDetailModal id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
        </AdminCard>
    );
};

export default CampaignList;
